import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { Star, Eye, Target, Shield, Radio, Stethoscope, FileText, GraduationCap, Building2, Users, TrendingUp, Hexagon, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import styles from './KnowledgeBase.module.css';
import ContextualHelp from '../components/ContextualHelp';

interface Article {
  id: number;
  slug: string;
  title: string;
  category: string;
  skillLevel?: string;
  viewCount: number;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  averageRating: number;
  ratingCount: number;
  author: {
    id: number;
    username: string;
    discordName?: string;
  };
  excerpt: string;
  cardImageUrl?: string;
}

interface ArticlesResponse {
  articles: Article[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const KnowledgeBase: React.FC = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced'];

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: 12 };
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSkillLevel) params.skillLevel = selectedSkillLevel;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get<ArticlesResponse>('knowledgebase/articles', { params });
      setArticles(response.data.articles);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get<string[]>('knowledgebase/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [page, selectedCategory, selectedSkillLevel, searchTerm]);

  const getCategoryIcon = (category: string) => {
    const iconMap: { [key: string]: React.ReactElement } = {
      flight: <Radio size={16} />,
      mining: <Hexagon size={16} />,
      salvage: <Hexagon size={16} />,
      cargo: <Hexagon size={16} />,
      logistics: <Hexagon size={16} />,
      'ground-ops': <Target size={16} />,
      security: <Shield size={16} />,
      medical: <Stethoscope size={16} />,
      sop: <FileText size={16} />,
      training: <GraduationCap size={16} />,
      organizational: <Building2 size={16} />,
      onboarding: <Users size={16} />,
      structure: <Building2 size={16} />,
      leadership: <TrendingUp size={16} />,
      organization: <Building2 size={16} />,
      operations: <Target size={16} />,
      exploration: <Radio size={16} />,
      intelligence: <Eye size={16} />,
    };
    return iconMap[category.toLowerCase()] || <FileText size={16} />;
  };

  const getSkillLevelBadge = (level?: string) => {
    if (!level) return null;
    const badgeClass = level.toLowerCase();
    return <span className={`${styles.badge} ${styles[badgeClass]}`}>{level}</span>;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
    fetchArticles();
  };

  return (
    <div className={styles.knowledgeBaseContainer}>
      <header className={styles.header}>
        <h1>
          <GraduationCap size={32} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} /> 
          Knowledge Base
          <span style={{ marginLeft: '0.5rem' }}>
            <ContextualHelp featureArea="knowledge-base" />
          </span>
        </h1>
        <p className={styles.subtitle}>
          Your centralized hub for Star Citizen gameplay, procedures, and training
        </p>
      </header>

      {/* Search and Filters */}
      <div className={styles.filterSection}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>
            <Search size={18} /> Search
          </button>
        </form>

        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className={styles.filterSelect}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {getCategoryIcon(cat)} {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Skill Level:</label>
            <select
              value={selectedSkillLevel}
              onChange={(e) => {
                setSelectedSkillLevel(e.target.value);
                setPage(1);
              }}
              className={styles.filterSelect}
            >
              <option value="">All Levels</option>
              {skillLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div className={styles.loading}>Loading articles...</div>
      ) : articles.length === 0 ? (
        <div className={styles.emptyState}>
          {!user ? (
            <>
              <h3>Knowledge Base Access Required</h3>
              <p>The Knowledge Base is only accessible to registered users.</p>
              <p>Please <Link to="/login">log in</Link> to view training materials, guides, and documentation.</p>
            </>
          ) : (
            <>
              <p>No articles found matching your criteria.</p>
              {(selectedCategory || selectedSkillLevel || searchTerm) && (
                <button
                  onClick={() => {
                    setSelectedCategory('');
                    setSelectedSkillLevel('');
                    setSearchTerm('');
                    setPage(1);
                  }}
                  className={styles.clearFiltersButton}
                >
                  Clear Filters
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className={styles.articlesGrid}>
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/knowledge/${article.slug}`}
                className={styles.articleCard}
              >
                {article.cardImageUrl && (
                  <div 
                    className={styles.cardImage}
                    style={{ backgroundImage: `url(${article.cardImageUrl})` }}
                  />
                )}
                <div className={styles.articleHeader}>
                  <span className={styles.categoryBadge}>
                    {getCategoryIcon(article.category)} {article.category}
                  </span>
                  {getSkillLevelBadge(article.skillLevel)}
                </div>
                
                <h3 className={styles.articleTitle}>{article.title}</h3>
                
                <p className={styles.articleExcerpt}>{article.excerpt}</p>
                
                <div className={styles.articleFooter}>
                  <div className={styles.articleMeta}>
                    <span className={styles.author}>
                      By {article.author.discordName || article.author.username}
                    </span>
                    <div className={styles.cardRating}>
                      <Star
                        size={16}
                        fill={article.averageRating > 0 ? 'var(--warning-color)' : 'none'}
                        stroke="var(--warning-color)"
                      />
                      <span className={styles.ratingText}>
                        {article.averageRating > 0 ? article.averageRating.toFixed(1) : 'No ratings'}
                        {article.ratingCount > 0 && ` (${article.ratingCount})`}
                      </span>
                    </div>
                    <span className={styles.views}>
                      <Eye size={16} /> {article.viewCount}
                    </span>
                  </div>
                  
                  {article.tags && article.tags.length > 0 && (
                    <div className={styles.tags}>
                      {article.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={styles.paginationButton}
              >
                ← Previous
              </button>
              
              <span className={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={styles.paginationButton}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KnowledgeBase;
